<script>
import { AbcToggle } from "@ordbogen-npm/abc-dsk";
import { mapStores } from "pinia";

import TgSavedToast from "@/abc-dsk/components/SavedToast/TgSavedToast.vue";
import ImageResponsive from "@/components/ImageResponsive.vue";
import { MatomoHelper } from "@/helpers";
import { useSettingsStore } from "@/stores";
import { COLORBLIND_THEME, DEFAULT_FONT, DEFAULT_THEME, OPENDYSLEXIC_FONT } from "@/theming";
import { MatomoEventAction, MatomoEventCategory } from "@/types";

export default {
  name: "AccessibilitySettings",
  components: {
    AbcToggle,
    ImageResponsive,
    TgSavedToast
  },
  data() {
    return {
      updatedFontToast: false,
      updatedThemeToast: false
    };
  },
  computed: {
    ...mapStores(useSettingsStore),

    useColorblindTheme() {
      return this.theme === COLORBLIND_THEME;
    },

    useOpenDyslexicFont() {
      return this.font === OPENDYSLEXIC_FONT;
    },

    theme() {
      return this.settingsStore.userPreferences.theme;
    },

    font() {
      return this.settingsStore.userPreferences.articleFont;
    }
  },
  methods: {
    async toggleColorblindTheme(value) {
      this.updatedThemeToast = false;

      const theme = value === true ? COLORBLIND_THEME : DEFAULT_THEME;
      await this.settingsStore.setTheme(theme);

      this.updatedThemeToast = true;

      MatomoHelper.sendEvent(
        MatomoEventCategory.AbcSettings,
        MatomoEventAction.AccessabilitySettings,
        `Toggled colorblind mode to ${value}`
      );
    },
    async toggleOpenDyslexicFont(value) {
      this.updatedFontToast = false;

      const font = value === true ? OPENDYSLEXIC_FONT : DEFAULT_FONT;
      await this.settingsStore.setArticleFont(font);

      this.updatedFontToast = true;

      MatomoHelper.sendEvent(
        MatomoEventCategory.AbcSettings,
        MatomoEventAction.AccessabilitySettings,
        `Toggled OpenDyslexic to ${value}`
      );
    }
  }
};
</script>

<template>
  <div>
    <div class="d-none d-lg-block settings-group">
      <h2 class="settings-group__heading settings-group__heading--big mb-0">
        {{ $t("settings.accessibility.title") }}
      </h2>
    </div>
    <hr class="settings-group-divider d-none d-lg-block" />
    <div class="settings-group row">
      <div class="col-12 col-lg-6 settings-group__content settings-group__content--vertical-lg">
        <div class="settings-group__details settings-group__details--vertical-lg">
          <h3 class="settings-group__heading">{{ $t("settings.accessibility.fontHeading") }}</h3>
          <p class="settings-group__subheading">
            {{ $t("settings.accessibility.fontSubheading") }}
          </p>
        </div>
        <div class="settings-group__toast-container settings-group__toast-container--centered">
          <TgSavedToast
            v-model:show="updatedFontToast"
            :top="-16"
            :left="-24"
            absolute
            class="d-lg-none"
          />
          <AbcToggle
            :model-value="useOpenDyslexicFont"
            :aria-label="$t('settings.accessibility.fontHeading')"
            @update:model-value="toggleOpenDyslexicFont($event)"
          />
          <TgSavedToast v-model:show="updatedFontToast" class="d-none d-lg-flex ml-3" />
        </div>
      </div>
      <div class="col-12 col-lg-6">
        <ImageResponsive
          src="/assets/img/settings/opendyslexic-preview@1x.png"
          class="d-lg-none"
          presentational
        />
        <ImageResponsive
          src="/assets/img/settings/opendyslexic-preview-grey@1x.png"
          class="d-none d-lg-flex justify-content-end mb-3"
          presentational
        />
      </div>
    </div>
    <hr class="settings-group-divider" />
    <div class="settings-group row">
      <div class="col-12 col-lg-6 settings-group__content settings-group__content--vertical-lg">
        <div class="settings-group__details settings-group__details--vertical-lg">
          <h3 class="settings-group__heading">
            {{ $t("settings.accessibility.colorblindHeading") }}
          </h3>
          <p class="settings-group__subheading">
            {{ $t("settings.accessibility.colorblindSubheading") }}
          </p>
        </div>
        <div class="settings-group__toast-container settings-group__toast-container--centered">
          <TgSavedToast
            v-model:show="updatedThemeToast"
            :top="-16"
            :left="-24"
            absolute
            class="d-lg-none"
          />
          <AbcToggle
            :model-value="useColorblindTheme"
            :aria-label="$t('settings.accessibility.colorblindHeading')"
            @update:model-value="toggleColorblindTheme($event)"
          />
          <TgSavedToast v-model:show="updatedThemeToast" class="d-none d-lg-flex ml-3" />
        </div>
      </div>
      <div class="col-12 col-lg-6 mb-3">
        <ImageResponsive
          src="/assets/img/settings/colorblind-preview@1x.png"
          class="d-lg-none"
          presentational
        />
        <ImageResponsive
          src="/assets/img/settings/colorblind-preview-grey@1x.png"
          class="d-none d-lg-flex justify-content-end"
          presentational
        />
      </div>
    </div>
  </div>
</template>
